import { memo, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { ImageOff, Loader2 } from 'lucide-react'

import { useElementWidth } from '@/hooks/use-ui'
import { preferences, useStore, type Density } from '@/lib/store'
import { cn } from '@/lib/utils'

export interface GridItem {
  id: string
  path: string
}

function columnsFor(width: number, density: Density): number {
  const base = width < 480 ? 2 : width < 768 ? 3 : width < 1100 ? 4 : width < 1500 ? 5 : 6
  return density === 'compact' ? base + 1 : base
}

/**
 * Window-scrolled, row-virtualised grid. Window scrolling (instead of an inner
 * scroll box) keeps mobile browsers' collapsing address bar and native
 * scroll restoration working, and only the rows near the viewport are mounted
 * even after thousands of images.
 */
export function ImageGrid<T extends GridItem>({
  items,
  hasMore,
  isFetchingMore,
  onLoadMore,
  renderOverlay,
}: {
  items: T[]
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
  renderOverlay?: (item: T) => ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const width = useElementWidth(ref)
  const { density } = useStore(preferences)
  const [offset, setOffset] = useState(0)

  const columns = Math.max(2, columnsFor(width, density))
  const gap = width < 640 ? 6 : 12
  const tile = width ? (width - gap * (columns - 1)) / columns : 200
  const rowHeight = Math.round(tile * 1.3) + gap
  const rows = Math.ceil(items.length / columns)

  useLayoutEffect(() => {
    if (ref.current) setOffset(ref.current.getBoundingClientRect().top + window.scrollY)
  }, [width])

  const virtualizer = useWindowVirtualizer({
    count: rows,
    estimateSize: () => rowHeight,
    overscan: 4,
    scrollMargin: offset,
  })

  useEffect(() => {
    virtualizer.measure()
  }, [rowHeight, virtualizer])

  const virtualRows = virtualizer.getVirtualItems()
  const lastRow = virtualRows[virtualRows.length - 1]?.index ?? 0

  useEffect(() => {
    if (hasMore && !isFetchingMore && rows > 0 && lastRow >= rows - 4) onLoadMore()
  }, [lastRow, rows, hasMore, isFetchingMore, onLoadMore])

  return (
    <div ref={ref}>
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {width > 0 &&
          virtualRows.map((row) => (
            <div
              key={row.key}
              className="absolute inset-x-0 top-0 grid"
              style={{
                transform: `translateY(${row.start - virtualizer.options.scrollMargin}px)`,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap,
                height: rowHeight - gap,
              }}
            >
              {items.slice(row.index * columns, row.index * columns + columns).map((item, i) => (
                <ImageTile key={item.id} item={item} delay={i * 30} overlay={renderOverlay?.(item)} />
              ))}
            </div>
          ))}
      </div>

      <div className="flex h-20 items-center justify-center text-sm text-muted-foreground">
        {isFetchingMore ? (
          <span className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Loading more…
          </span>
        ) : !hasMore && items.length > 0 ? (
          <span>You’ve reached the end</span>
        ) : null}
      </div>
    </div>
  )
}

const ImageTile = memo(function ImageTile({
  item,
  delay,
  overlay,
}: {
  item: GridItem
  delay: number
  overlay?: ReactNode
}) {
  const location = useLocation()
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>(item.path ? 'loading' : 'error')

  return (
    <Link
      to={`/imgb/${item.id}`}
      state={{ backgroundLocation: location }}
      className="group relative block overflow-hidden rounded-xl bg-muted ring-1 ring-inset ring-border/60 outline-offset-2 sm:rounded-2xl"
      aria-label="Open image"
    >
      {state === 'loading' && <span className="skeleton absolute inset-0" aria-hidden />}
      {state === 'error' && !overlay && (
        <span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
          <ImageOff className="size-6" />
        </span>
      )}
      {item.path && state !== 'error' && (
        <img
          src={item.path}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
          style={{ transitionDelay: `${Math.min(delay, 200)}ms` }}
          className={cn(
            'size-full object-cover transition-[opacity,transform] duration-500 ease-out group-hover:scale-[1.04]',
            state === 'loaded' ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      />
      {overlay}
    </Link>
  )
})
